import { Location } from '@angular/common';
import { Component, computed, inject, signal, untracked } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { SelectButton } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import {
  appRouteCommands,
  appRoutePaths,
  HeaderSectionRoutePath,
} from '../../../app-route-paths';
import {
  agentRouteCommands,
  openAgentTab,
  SALES_MODULE_KEY,
} from '../../../agent/agent-route-paths';
import { AuthService } from '../../../services/auth-service';
import {
  activitiesSectionKeys,
  AppSectionKey,
} from '../../../guards/section-visibility.config';
import { SectionVisibilityService } from '../../../services/section-visibility.service';
import { DiagnosticService } from '../../../services/diagnostic.service';
import { COMPANY } from '../../../providers/company.provider';
import { CompanyService } from '../../../services/company.service';
import { PermissionResource } from '../../../connectors/backend';
import { isAdminUser } from '../../../guards/admin.guard';
import { PermissionsService } from '../../../services/permissions.service';
import { Avatar } from '../../atoms/avatar/avatar';
import { Menu } from '../../molecules/menu/menu';
import { MenuContent } from '../../molecules/menu/menu-content/menu-content';
import { MenuItem } from '../../molecules/menu/menu-item/menu-item';
import { diagnosticQueryKeys } from '../../../services/query-keys';

type Section = HeaderSectionRoutePath;
const headerSectionByRouteKey: Record<string, Section> = {
  [appRoutePaths.productsDrafts]: appRoutePaths.productsDrafts,
  [appRoutePaths.productsApproved]: appRoutePaths.productsApproved,
  [appRoutePaths.dam]: appRoutePaths.dam,
  [appRoutePaths.activities]: appRoutePaths.activities,
};

interface NavigationItem {
  value: Section;
  label: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.html',
  imports: [
    Avatar,
    Button,
    SelectButton,
    FormsModule,
    Menu,
    MenuContent,
    MenuItem,
    ReactiveFormsModule,
    Select,
    TooltipModule,
  ],
})
export class Header {
  readonly auth = inject(AuthService);
  readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly _company = inject(COMPANY);
  private readonly companyService = inject(CompanyService);
  readonly diagnosticService = inject(DiagnosticService);
  readonly userService = inject(AuthService);
  private readonly sectionVisibility = inject(SectionVisibilityService);
  private readonly permissions = inject(PermissionsService);
  readonly appReleaseVersion = RELEASE_VERSION;

  readonly me = this.userService.me;
  readonly isVersionEnabled = signal(false);

  readonly diagnosticQuery = injectQuery(() => ({
    queryKey: diagnosticQueryKeys.version(),
    queryFn: () => this.diagnosticService.version(),
  }));

  readonly company = computed(() => {
    const companyId = this._company();
    const list = this.companies();
    const company = list.find((c) => c.id === companyId);
    const firstOfList = list.at(0);
    if (company) {
      untracked(() => this.companyService.setCompanyId(company.id));
    } else if (firstOfList) {
      untracked(() => this.companyService.setCompanyId(firstOfList.id));
    }
    return company?.id ?? firstOfList?.id;
  });

  readonly apiVersionTooltip = computed(() => {
    const version = this.diagnosticQuery.data();
    return $localize`API v.${version ?? ''}`;
  });

  readonly companies = this.companyService.companies;
  readonly companiesLoading = this.companyService.companiesQuery.isLoading;

  /** Content culture each company opens in, shown next to its name. */
  readonly contentCultures = computed(
    () =>
      new Map(
        this.companies().map((c) => [
          c.id,
          this.companyService.contentCultureFor(c.id),
        ]),
      ),
  );

  readonly initials = computed(() => {
    const user = this.me();
    const nameInitial = user?.firstName?.at(0);
    const lastNameInitial = user?.lastName?.at(0);

    if (!nameInitial || !lastNameInitial) return undefined;

    return `${nameInitial}${lastNameInitial}`.toUpperCase();
  });

  readonly name = computed(() => {
    const { firstName, lastName } = this.me() ?? {};

    if (!firstName || !lastName) return undefined;

    return `${firstName} ${lastName}`;
  });

  readonly avatarImage = computed(() => this.me()?.base64Avatar ?? undefined);

  readonly email = computed(() => this.me()?.email ?? undefined);

  get currentSection(): Section | undefined {
    const segments = this.router.url.split('?')[0].split('/').filter(Boolean);
    const routeKey = segments.slice(0, 2).join('/');

    if (segments[0] === 'dam') {
      return appRoutePaths.dam;
    } else if (segments[0] === 'lists') {
      return undefined;
    }

    // A section can own child routes (`activities/imports`), so the two-segment
    // key is tried first and the section root second.
    return (
      headerSectionByRouteKey[routeKey] ??
      headerSectionByRouteKey[segments[0]] ??
      appRoutePaths.productsDrafts
    );
  }

  readonly navigationItems = computed(() => {
    const items: NavigationItem[] = [];

    if (
      this.sectionVisibility.canAccessSection(
        AppSectionKey.ProductAndProductSheet,
      )
    ) {
      items.push({
        value: appRoutePaths.productsApproved,
        label: $localize`Approvati`,
      });
      items.push({
        value: appRoutePaths.productsDrafts,
        label: $localize`Bozze`,
      });
    }

    if (this.sectionVisibility.canAccessSection(AppSectionKey.Dam)) {
      items.push({
        value: appRoutePaths.dam,
        label: $localize`DAM`,
      });
    }

    if (this.sectionVisibility.canAccessAnySection(activitiesSectionKeys)) {
      items.push({
        value: appRoutePaths.activities,
        label: $localize`Attività`,
      });
    }

    return items;
  });

  /** Admins and company admins get a settings shortcut next to the avatar. */
  readonly canManageSettings = computed(
    () =>
      isAdminUser(this.me()?.roles) ||
      this.permissions.canUpdateAll(PermissionResource.User) ||
      this.permissions.canUpdateAll(PermissionResource.Role),
  );

  readonly isSalesRep = computed(() => {
    const companyId = this._company();
    return (
      !!companyId &&
      !!this.me()?.companies[companyId]?.modules.includes(SALES_MODULE_KEY)
    );
  });

  async onCompanyChange(companyId: string): Promise<void> {
    if (companyId === this.company()) return;

    // Back to the root section: whatever product or asset is open belongs to
    // the company being left, and its id means nothing under the new one.
    await this.companyService.navigateInCompany(
      companyId,
      appRouteCommands.root(),
    );
  }

  goToPage(section: Section): void {
    // Search, filters and page belong to the section being left: the next one
    // opens clean rather than inheriting a query it cannot read.
    this.router.navigate(appRouteCommands.fromPath(section), {
      queryParams: {},
    });
  }

  async logOut() {
    this.auth.logout();
    await this.router.navigate(appRouteCommands.root());
  }

  /** Opens the settings on channels; `adminGuard` sends anyone else to account. */
  goToSettings(): void {
    this.router.navigate(appRouteCommands.settingsChannels());
  }

  openAgentApp(): void {
    openAgentTab(this.router, this.location, agentRouteCommands.dashboard());
  }

  goToAccount(): void {
    this.router.navigate(appRouteCommands.settingsAccount());
  }
}
